import { useQuery } from '@tanstack/react-query';
import { AccountMenu } from './AccountMenu';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BrandMark } from './BrandMark';
import { RepertoireManager } from './RepertoireManager';
import { SiteFooter } from './SiteFooter';
import { UsernameOnboarding } from './UsernameOnboarding';
import { useApi } from '../api/api-context';
import { useAuth } from '../api/auth-context';

/**
 * Global app shell (final-look direction §2): brand, main nav, workspace + sign
 * out, and the site footer. The active page renders through <Outlet /> and keeps
 * its own <main>.
 */
export function AppShell(): JSX.Element {
  const auth = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => api.getProfile() });
  // A null username means onboarding is incomplete (spec v1.2): choose one first.
  const needsUsername = profile.data?.username === null;

  function onSignOut(): void {
    auth.logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="app-shell dark-shell site-frame">
      <header className="topbar">
        <Link to="/" className="brand" aria-label="ItsAllPrep home">
          <BrandMark size={28} />
          <span className="brand-name">ItsAllPrep</span>
        </Link>
        <RepertoireManager
          activeId={pathname.startsWith('/repertoires/') ? pathname.split('/')[2] : undefined}
        />
        <nav className="topnav" aria-label="Main">
          <Link
            to="/"
            aria-current={
              pathname === '/' || pathname.startsWith('/repertoires/') ? 'page' : undefined
            }
          >
            Repertoires
          </Link>
          <Link to="/games" aria-current={pathname.startsWith('/games') ? 'page' : undefined}>
            Game review
          </Link>
        </nav>
        <div className="topbar-right">
          <AccountMenu key={auth.token} />
          <button type="button" className="topbar-signout" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>
      <div className="site-frame-body">
        {needsUsername ? <UsernameOnboarding /> : <Outlet />}
      </div>
      <SiteFooter />
    </div>
  );
}
